/**
 * Past-day navigation (A7Y-3872, D14): a date picker that opens the best run
 * for a chosen day, and the banner shown while a past run is open.
 *
 * "Best run for a day" mirrors what a viewer actually wants to see: the
 * day's own checkpoint (a schedule or manual run — the trigger kinds that
 * become the day's canonical card) with the latest `completedAt` if more
 * than one ran, else the latest successful refresh, else nothing. A day with
 * no successful run of any kind shows `helm-day-empty` rather than silently
 * opening the wrong thing.
 */
import { useState } from 'react';
import type { HelmRunSummary } from '@a7y/platform-health/helm';

function bestRunForDay(runs: HelmRunSummary[], day: string): HelmRunSummary | null {
  const succeeded = runs.filter((r) => r.localDate === day && r.status === 'succeeded');
  const byCompletedDesc = (a: HelmRunSummary, b: HelmRunSummary) =>
    (b.completedAt ?? '').localeCompare(a.completedAt ?? '');
  const checkpoint = succeeded
    .filter((r) => r.trigger === 'schedule' || r.trigger === 'manual')
    .sort(byCompletedDesc)[0];
  if (checkpoint) return checkpoint;
  const refresh = succeeded.filter((r) => r.trigger === 'refresh').sort(byCompletedDesc)[0];
  return refresh ?? null;
}

/** Today's Madrid-local date as `YYYY-MM-DD` (Karen round 1 F13 — the day
 * picker's `max` must not be the browser's own UTC/local date, since
 * `localDate` is itself Madrid-derived server-side, D20). `en-CA` formats as
 * ISO order directly, so no manual field reassembly is needed. */
function madridToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid' }).format(new Date());
}

/** `Wed 23 Sep` — mirrors `formatHelmDay` in `scripts/helm/cards.cjs`, which
 * a7y-admin cannot import (that module sits outside the `contract.ts`-only
 * alias, plan D1); `localDate` is already a plain `YYYY-MM-DD`, so this reads
 * it as UTC to avoid the browser's own zone shifting the calendar day. */
function formatDay(localDate: string): string {
  const d = new Date(`${localDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return localDate;
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
  const day = d.toLocaleDateString('en-US', { day: '2-digit', timeZone: 'UTC' });
  const month = d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${weekday} ${day} ${month}`;
}

export function DayPicker({
  runs,
  onOpenRun,
}: {
  runs: HelmRunSummary[];
  onOpenRun: (runId: string) => void;
}) {
  const [selected, setSelected] = useState('');
  const [empty, setEmpty] = useState(false);

  const localDates = runs
    .map((r) => r.localDate)
    .filter(Boolean)
    .sort();
  const min = localDates[0];
  const max = madridToday();

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <label className="flex items-center gap-2">
        <span className="text-xs text-zinc-500">View a past day</span>
        <input
          type="date"
          data-testid="helm-day-picker"
          min={min}
          max={max}
          value={selected}
          onChange={(e) => {
            const day = e.target.value;
            setSelected(day);
            if (!day) {
              setEmpty(false);
              return;
            }
            const best = bestRunForDay(runs, day);
            if (best) {
              setEmpty(false);
              onOpenRun(best.runId);
            } else {
              setEmpty(true);
            }
          }}
          className="min-h-11 rounded border border-zinc-300 px-2 dark:border-zinc-700 dark:bg-zinc-950"
        />
      </label>
      {empty && (
        <span data-testid="helm-day-empty" className="text-xs text-zinc-500">
          No successful run on that day.
        </span>
      )}
    </div>
  );
}

export function PastDayBanner({
  summary,
  onBack,
}: {
  summary: HelmRunSummary;
  onBack: () => void;
}) {
  return (
    <div
      data-testid="helm-past-banner"
      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-blue-300 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300"
    >
      <span className="min-w-0 break-words">
        Showing {formatDay(summary.localDate)} · {summary.trigger} run · read-only, actions as they
        stood then
      </span>
      <button
        type="button"
        data-testid="helm-back-to-today"
        onClick={onBack}
        className="min-h-11 rounded-md border border-blue-300 px-3 text-sm font-medium text-blue-800 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900"
      >
        Back to today
      </button>
    </div>
  );
}
